import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import ParameterDetails from 'Services/ProductManagement/Features/Parameter/Components/ParameterDetails'
import { ParameterGroupList } from 'Services/ProductManagement/Features/Parameter/Components/ParameterGroupList'
import { useParameterGroups } from 'Services/ProductManagement/Features/Parameter/Hooks'
import { useUpdateSingleParameterGroupMutation } from 'Services/ProductManagement/Features/Parameter/parameterApi'
import type {
  ParameterGroup,
  ParameterGroupParameter
} from 'Services/ProductManagement/Features/Parameter/types'
import { PRODUCT_GROUP_CATEGORY, PRODUCT_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

function ParameterGroups({
  singleParameterGroup,
  isLoading
}: Readonly<{
  singleParameterGroup: ParameterGroup | null
  isLoading: boolean
}>) {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/settings/product-parameters`
  const { confirm } = useConfirm()
  const navigate = useNavigate()

  const { selectedParameterId, setSelectedParameterId } = useParameterGroups()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const [parameterGroupParameterDetails, setParameterGroupParameterDetails] = useState<
    ParameterGroupParameter | undefined
  >(undefined)

  const [updateSingleParameterGroup, { isLoading: submitLoading }] =
    useUpdateSingleParameterGroupMutation()
  const parameterGroupParameters = singleParameterGroup ? [...singleParameterGroup.parameters] : []

  useEffect(() => {
    if (singleParameterGroup && selectedParameterId) {
      const parameterGroupParameter = singleParameterGroup?.parameters.find(
        parameter => parameter.parameter_id === selectedParameterId
      )
      setParameterGroupParameterDetails(parameterGroupParameter)
    }
  }, [selectedParameterId])

  const handleDrawerClose = () => {
    setSelectedParameterId('')
    setIsDrawerOpen(false)
  }

  const handleRowClick = (id: string) => {
    setSelectedParameterId(id)
    setIsDrawerOpen(true)
  }

  /**
   * Remove a parameter group parameter from the array based on a matching parameterId.
   */
  const removeParameterFromGroupParameters = () => {
    // Find the index of the parameter group parameter with the matching parameterId
    const foundParameterGroupParameterIndex = parameterGroupParameters.findIndex(
      parameterGroupParameter => parameterGroupParameter.parameter_id === selectedParameterId
    )

    // Remove found index from the parameter group parameters array
    if (foundParameterGroupParameterIndex !== -1) {
      parameterGroupParameters.splice(foundParameterGroupParameterIndex, 1)
    }
  }

  const handleDelete = () => {
    if (!singleParameterGroup) {
      SnackbarUtils.error(t`There was an error deleting the parameter.`)
      return
    }

    removeParameterFromGroupParameters()
    const updatedParameterGroup: ParameterGroup = {
      ...singleParameterGroup,
      parameters: parameterGroupParameters
    }
    const params = {
      parameterGroupName: singleParameterGroup.category,
      payload: updatedParameterGroup
    }
    updateSingleParameterGroup(params)
      .unwrap()
      .then(() => {
        handleDrawerClose()
        SnackbarUtils.success(t`Parameter successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the parameter.`)
      })
  }

  const isProductGroupCategory = singleParameterGroup?.category === PRODUCT_GROUP_CATEGORY
  const isUpdateParameterGroupLoading = submitLoading

  return (
    <WithLoader loading={isUpdateParameterGroupLoading}>
      <ParameterGroupList
        rows={singleParameterGroup?.parameters as ParameterGroupParameter[]}
        parameterGroup={singleParameterGroup}
        isLoading={isLoading}
        link={`${baseUrl}/${singleParameterGroup?.category}`}
        onRowClick={handleRowClick}
      />
      <DrawerRight
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        topBar={
          <Box>
            {isProductGroupCategory || (
              <DeleteButton
                sx={{ mr: 1 }}
                size="small"
                onClick={() =>
                  confirm({
                    title: t`Are you sure?`,
                    message: t`Are you sure you want to delete the parameter?`,
                    handleConfirm: () => {
                      handleDelete()
                    }
                  })
                }
              >{t`Delete`}</DeleteButton>
            )}
            <CustomButton
              onClick={() =>
                navigate(`${baseUrl}/${singleParameterGroup?.category}/${selectedParameterId}`)
              }
              size="small"
              startIcon={<EditIcon />}
            >{t`Edit`}</CustomButton>
          </Box>
        }
      >
        {parameterGroupParameterDetails && (
          <TabContext value={'details'}>
            <TabList aria-label={t`Parameter tab list`}>
              <Tab label={t`Parameter details`} value={'details'} />
            </TabList>
            <Divider />
            <TabPanel sx={{ p: 0 }} value={'details'}>
              <ParameterDetails parameterGroupParameter={parameterGroupParameterDetails} />
            </TabPanel>
          </TabContext>
        )}
      </DrawerRight>
    </WithLoader>
  )
}

export default ParameterGroups
